import PropTypes from 'prop-types';
import React from 'react';
import { cleanState } from '@ublend-npm/aula-editor';
import ResizeObserver from 'resize-observer-polyfill';
import Reply from '../Reply.container';
import { Container, EditorWrapper } from './WriteZone.styled';
import { UP_ARROW } from '../../../../../../utils/keyCodes';
import AulaEditorWithAnalyticsWithoutPlugins from '../../../../common/AulaEditor/AulaEditorWithAnalyticsWithoutPlugins';

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// TODO move to utils when TH90 is deprecated
const isEmptyEditorContent = (content) => {
  // Check if content or value is missing
  if (!content || !content.value) {
    return true;
  }

  // Check if value is empty array
  if (content.value.length === 0) {
    return true;
  }

  // To Check if any file content exist
  const hasFile = content.value.some(
    (node) => node.type === 'fileBlock' || node.type === 'code'
  );
  if (hasFile) {
    return false;
  }

  // Check if all nodes are empty
  const hasContent = content.value.some((node) => {
    if (!node.children || node.children.length === 0) {
      return false;
    }

    return node.children.some((child) => {
      // Check if child has text content (not just whitespace)
      return child.text && child.text.trim() !== '';
    });
  });

  return !hasContent;
};

class WriteZone extends React.PureComponent {
  constructor(props) {
    super(props);
    this.editorRef = React.createRef();
    this.editorWrapperRef = React.createRef();
    this.state = {
      refreshEditorKey: 0,
      editorState: cleanState,
      isSubmitting: false,
      isUpdatingFiles: false,
      hasFileErrors: false,
      isPersistable: true,
    };
    this.onChange = this.onChange.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onReturn = this.onReturn.bind(this);
    this.focus = this.focus.bind(this);
    this.updateContent = this.updateContent.bind(this);
    this.clear = this.clear.bind(this);
    this.loadContentAndFocusEditor = this.loadContentAndFocusEditor.bind(this);
    this.clearAndFocusEditor = this.clearAndFocusEditor.bind(this);
    this.setSubmittingState = this.setSubmittingState.bind(this);
  }

  componentDidMount() {
    const { onHeightChange } = this.props;
    this.loadContentAndFocusEditor();
    this.ob = new ResizeObserver(onHeightChange);
    if (this.editorWrapperRef.current) {
      this.ob.observe(this.editorWrapperRef.current);
    }
  }

  async componentDidUpdate(prevProps) {
    const { conversationId, reply } = this.props;
    const conversationChanged = prevProps.conversationId !== conversationId;
    const replyChanged =
      reply !== prevProps.reply || reply?.id !== prevProps.reply?.id;

    if (conversationChanged) {
      this.updateContent(prevProps.conversationId);
      this.loadContentAndFocusEditor();
    }
    if (replyChanged) {
      await this.focus();
    }
  }

  componentWillUnmount() {
    const { conversationId } = this.props;
    this.updateContent(conversationId);
    this.ob.unobserve(this.editorWrapperRef.current);
  }

  setSubmittingState(isSubmitting) {
    this.setState((prevState) => ({
      ...prevState,
      isSubmitting,
    }));
  }

  onChange(editorState, conversationId) {
    const { sendTyping, conversationId: conversationIdProp } = this.props;
    if (conversationId === conversationIdProp) {
      this.setState((prevState) => ({
        ...prevState,
        editorState,
      }));

      if (isEmptyEditorContent(editorState)) {
        sendTyping();
      }
    }
  }

  onKeyDown(event) {
    const { keyCode } = event;
    const { onEdit } = this.props;
    const { editorState } = this.state;
    if (keyCode === UP_ARROW && isEmptyEditorContent(editorState)) {
      onEdit();
    }
  }

  onReturn(editorState, conversationId) {
    const { onReturn, onMessageSubmit, messageType } = this.props;
    const { isSubmitting, isUpdatingFiles, hasFileErrors, isPersistable } =
      this.state;

    // Prevent sending if already submitting, files are uploading/scanning, or have errors
    if (isSubmitting || isUpdatingFiles || hasFileErrors || !isPersistable)
      return;
    // Prevent sending blank/empty messages
    if (isEmptyEditorContent(editorState)) {
      return;
    }

    onReturn(editorState, conversationId, messageType);
    onMessageSubmit();
    this.clearAndFocusEditor();
  }

  clear() {
    const { refreshEditorKey } = this.state;
    this.setState({
      refreshEditorKey: refreshEditorKey + 1,
      editorState: cleanState,
    });
  }

  updateContent(conversationId) {
    const { editorState } = this.state;
    const exportedContent = !isEmptyEditorContent(editorState)
      ? editorState
      : undefined;
    const { updateContent } = this.props;
    updateContent(conversationId, exportedContent);
  }

  async clearAndFocusEditor() {
    this.clear();
    await this.focus();
  }

  async loadContentAndFocusEditor() {
    const { content } = this.props;
    const { refreshEditorKey } = this.state;
    if (content) {
      this.setState({
        refreshEditorKey: refreshEditorKey + 1,
        editorState: content,
      });
    } else {
      this.clear();
    }
    await this.focus();
  }

  async focus() {
    await delay(50);
    const { focus } = this.editorRef.current ? this.editorRef.current : {};
    focus && focus();
  }

  render() {
    const { placeholder, reply, htmlAttributes, editorConfig, conversationId } =
      this.props;
    const { refreshEditorKey, editorState, isSubmitting } = this.state;

    return (
      <Container
        role="textbox"
        onKeyDown={this.onKeyDown}
        tabIndex={-1}
        {...htmlAttributes}
      >
        {reply && <Reply />}
        <EditorWrapper>
          <div ref={this.editorWrapperRef}>
            <AulaEditorWithAnalyticsWithoutPlugins
              ref={this.editorRef}
              config={editorConfig}
              editorState={editorState}
              key={refreshEditorKey}
              placeholder={placeholder}
              onFilesStatusChange={({
                isUpdating,
                isPersistable,
                hasError,
              }) => {
                this.setState({
                  isUpdatingFiles: isUpdating,
                  hasFileErrors: hasError,
                  isPersistable,
                });
              }}
              type="inbox"
              onChange={this.onChange}
              onReturn={this.onReturn}
              isSubmitting={isSubmitting}
              setSubmittingState={this.setSubmittingState}
              conversationId={conversationId}
            />
          </div>
        </EditorWrapper>
      </Container>
    );
  }
}

WriteZone.propTypes = {
  conversationId: PropTypes.string.isRequired,
  onEdit: PropTypes.func.isRequired,
  onHeightChange: PropTypes.func.isRequired,
  onMessageSubmit: PropTypes.func.isRequired,
  onReturn: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  sendTyping: PropTypes.func.isRequired,
  content: PropTypes.shape({}),
  updateContent: PropTypes.func.isRequired,
  reply: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  htmlAttributes: PropTypes.shape({}),
  editorConfig: PropTypes.shape({
    filesUrl: PropTypes.string,
    apiUrl: PropTypes.string,
  }).isRequired,
  messageType: PropTypes.oneOf(['reply', 'direct']).isRequired,
};

WriteZone.defaultProps = {
  content: cleanState,
  reply: null,
  htmlAttributes: {},
};

export default WriteZone;
